import type { RequestFn } from '../http';

/**
 * KIAN Injection-Event-Log (Admin/Teamlead): jeder Prompt-Injection-Treffer
 * der KIAN-Pipelines — auch wenn nichts blockiert wurde. Review-Workflow:
 * prüfen, als False-Positive markieren oder freigeben (mit Re-Run ohne Block).
 */

export type KianInjectionStatus = 'OPEN' | 'REVIEWED' | 'FALSE_POSITIVE' | 'RELEASED';
export type KianInjectionAction = 'CONTENT_BLOCKED' | 'LLM_SKIPPED' | 'FLAGGED';

export interface KianInjectionEvent {
  id: string;
  createdAt: string;
  pipeline: string;
  source: string;
  riskScore: number;
  findings: string[];
  blocked: boolean;
  action: KianInjectionAction;
  refType: string | null;
  refId: string | null;
  refLabel: string | null;
  excerpt: string | null;
  status: KianInjectionStatus;
  reviewedAt: string | null;
  reviewedByName: string | null;
  reviewNote: string | null;
  notifiedAt: string | null;
}

export interface KianInjectionListResponse {
  events: KianInjectionEvent[];
  nextCursor: string | null;
  counts: Partial<Record<KianInjectionStatus, number>>;
}

export interface KianInjectionListParams {
  status?: KianInjectionStatus;
  pipeline?: string;
  limit?: number;
  cursor?: string;
}

export interface KianInjectionReleaseResponse {
  event: { id: string; status: KianInjectionStatus };
  rerun: 'ok' | 'unsupported' | 'failed';
  rerunError: string | null;
}

export function createKianInjectionsApi(request: RequestFn) {
  return {
    async listKianInjections(params: KianInjectionListParams = {}) {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.pipeline) qs.set('pipeline', params.pipeline);
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.cursor) qs.set('cursor', params.cursor);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<KianInjectionListResponse>(`/admin/kian-injections${suffix}`);
    },

    async reviewKianInjection(
      id: string,
      input: { status: 'REVIEWED' | 'FALSE_POSITIVE' | 'OPEN'; note?: string },
    ) {
      return request<{ event: { id: string; status: KianInjectionStatus } }>(
        `/admin/kian-injections/${id}/review`,
        { method: 'POST', body: JSON.stringify(input) },
      );
    },

    async releaseKianInjection(id: string, input: { note?: string } = {}) {
      return request<KianInjectionReleaseResponse>(`/admin/kian-injections/${id}/release`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
  };
}
